<template>
  <div class="upload-file">
    <el-upload
        multiple
        :action="uploadFileUrl"
        :before-upload="handleBeforeUpload"
        :file-list="fileList"
        :data="data"
        :limit="limit"
        :on-error="handleUploadError"
        :on-exceed="handleExceed"
        :on-success="handleUploadSuccess"
        :show-file-list="false"
        :headers="headers"
        class="upload-file-uploader"
        ref="fileUpload"
        v-if="!disabled"
    >
      <!-- 上传按钮 -->
      <el-button size="default" type="primary">选取文件</el-button>
      <!-- 上传提示 -->
      <template #tip>
        <div class="el-upload__tip" v-if="showTip">
          请上传
          <template v-if="fileSize"> 大小不超过 <b style="color: #f56c6c">{{ fileSize }}MB</b></template>
          <template v-if="fileType"> 格式为 <b style="color: #f56c6c">{{ fileType.join("/") }}</b></template>
          的文件
        </div>
      </template>
    </el-upload>

    <!-- 文件列表 -->
    <transition-group ref="uploadFileList" class="upload-file-list el-upload-list el-upload-list--text"
                      name="el-fade-in-linear" tag="ul">
      <li :key="file.uid" class="el-upload-list__item ele-upload-list__item-content" v-for="(file, index) in fileList">
        <el-link :href="file.url" :underline="false" target="_blank">
          <span class="el-icon-document" style="padding-left: 10px;color: #409EFF"> {{ getFileName(file.name) }} </span>
        </el-link>
        <div class="ele-upload-list__item-content-action">
          <el-link :underline="false" @click="handleDelete(index)" type="danger" v-if="!disabled">删除</el-link>
        </div>
      </li>
    </transition-group>
  </div>
</template>

<script>
import Sortable from 'sortablejs'
import { getToken } from "@/utils/auth";

export default {
  name: "FileUpload",
  props: {
    // 修复：v-model默认绑定modelValue，替代原来的value
    modelValue: {
      type: [String, Object, Array],
      default: ''
    },
    // 上传接口地址
    action: {
      type: String,
      default: "/file/upload"
    },
    // 上传携带的参数
    data: {
      type: Object
    },
    // 数量限制
    limit: {
      type: Number,
      default: 5
    },
    // 大小限制(MB)
    fileSize: {
      type: Number,
      default: 5
    },
    // 文件类型, 例如['png', 'jpg', 'jpeg']
    fileType: {
      type: Array,
      default: () => ["doc", "docx", "xls", "xlsx", "ppt", "pptx", "txt", "pdf", "ttf"]
    },
    // 是否显示提示
    isShowTip: {
      type: Boolean,
      default: true
    },
    // 禁用组件（仅查看文件）
    disabled: {
      type: Boolean,
      default: false
    },
    // 拖动排序
    drag: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      number: 0,
      uploadList: [],
      baseUrl: process.env.VUE_APP_BASE_API,
      uploadFileUrl: process.env.VUE_APP_BASE_API + this.action, // 上传文件服务器地址
      headers: {
        Authorization: "Bearer " + getToken(),
      },
      fileList: []
    }
  },
  mounted() {
    // 初始化fileList
    this.initFileList()

    if (this.drag && !this.disabled) {
      this.$nextTick(() => {
        const element = this.$refs.uploadFileList?.$el || this.$refs.uploadFileList
        if (element) {
          Sortable.create(element, {
            ghostClass: 'file-upload-darg',
            onEnd: (evt) => {
              const movedItem = this.fileList.splice(evt.oldIndex, 1)[0]
              this.fileList.splice(evt.newIndex, 0, movedItem)
              // 修复：触发正确的v-model更新事件
              this.emitModelValue()
            }
          })
        }
      })
    }
  },
  watch: {
    // 监听modelValue变化，同步到fileList
    modelValue: {
      handler(val) {
        this.initFileList(val)
      },
      deep: true,
      immediate: true
    }
  },
  computed: {
    // 是否显示提示
    showTip() {
      return this.isShowTip && (this.fileType || this.fileSize)
    },
  },
  methods: {
    // 初始化文件列表
    initFileList(val = this.modelValue) {
      try {
        if (!val) {
          this.fileList = [];
          return;
        }

        const list = Array.isArray(val) ? val : val.split(",");
        this.fileList = list.map((item, index) => {
          if (typeof item === "string") {
            // 确保uid唯一，避免v-for key重复
            return {
              name: this.getFileName(item),
              url: item,
              uid: `file-${index}-${Date.now()}`
            };
          }
          return { ...item, uid: item.uid || `file-${index}-${Date.now()}` };
        });
      } catch (error) {
        console.error("FileUpload: 数据初始化失败", error);
        this.fileList = [];
      }
    },
    // 上传前校检格式和大小
    handleBeforeUpload(file) {
      // 校检文件类型
      if (this.fileType) {
        const fileName = file.name.split('.')
        const fileExt = fileName[fileName.length - 1].toLowerCase() // 转小写，避免大小写问题
        const isTypeOk = this.fileType.includes(fileExt)
        if (!isTypeOk) {
          this.$modal.alert(`文件类型不支持！请上传${this.fileType.join("/")}格式文件!`, "warning", "文件类型错误")
          return false
        }
      }
      // 校检文件名是否包含特殊字符
      if (file.name.includes(',')) {
        this.$modal.alert('文件名不正确，不能包含英文逗号!', "warning", "文件名称错误")
        return false
      }
      // 校检文件大小
      if (this.fileSize) {
        const isLt = file.size / 1024 / 1024 < this.fileSize
        if (!isLt) {
          this.$modal.alert(`文件超出大小限制！上传文件大小不能超过 ${this.fileSize} MB!`, "warning", "文件大小错误")
          return false
        }
      }
      this.$modal.loading("正在上传文件，请稍候...")
      this.number++
      return true
    },
    // 文件个数超出
    handleExceed() {
      this.$modal.alert(`上传文件数量不能超过 ${this.limit} 个!`, "warning", "文件数量错误")
    },
    // 上传失败
    handleUploadError(err, file) {
      console.error("上传失败", err)
      this.$modal.alert("上传文件失败，请重试", "error", "文件上传失败")
      this.number--
      this.$modal.closeLoading()
      // 移除失败的文件
      this.$refs.fileUpload?.handleRemove(file)
      this.uploadedSuccessfully()
    },
    // 上传成功回调
    handleUploadSuccess(res, file) {
      if (res.code === 200 && res.data) {
        this.uploadList.push({
          name: res.data.name,
          url: res.data.url,
          uid: file.uid // 复用上传文件的uid，避免key重复
        })
      } else {
        this.number--
        this.$modal.closeLoading()
        this.$modal.msgError(res.msg || "上传失败")
        this.$refs.fileUpload?.handleRemove(file)
      }
      this.uploadedSuccessfully()
    },
    // 删除文件
    handleDelete(index) {
      this.fileList.splice(index, 1)
      // 触发v-model更新
      this.emitModelValue()
    },
    // 上传结束处理
    uploadedSuccessfully() {
      if (this.number > 0 && this.uploadList.length === this.number) {
        this.fileList = this.fileList.concat(this.uploadList)
        this.uploadList = []
        this.number = 0
        // 触发v-model更新
        this.emitModelValue()
        this.$modal.closeLoading()
      }
    },
    // 获取文件名称
    getFileName(name) {
      // 如果是url那么取最后的名字 如果不是直接返回
      if (name && name.lastIndexOf("/") > -1) {
        return name.slice(name.lastIndexOf("/") + 1)
      } else {
        return name || ''
      }
    },
    // 对象转成指定字符串分隔
    listToString(list, separator = ",") {
      if (!Array.isArray(list) || list.length === 0) return ''
      // 过滤空url的项
      const validList = list.filter(item => item.url)
      return validList.map(item => item.url).join(separator)
    },
    // 统一触发v-model更新事件
    emitModelValue() {
      const value = this.listToString(this.fileList)
      // 修复：触发Vue3 v-model的标准事件
      this.$emit('update:modelValue', value)
    }
  }
}
</script>

<style scoped>
.file-upload-darg {
  opacity: 0.5;
  background: #c8ebfb;
}

.upload-file-uploader {
  margin-bottom: 5px;
}

.upload-file-list .el-upload-list__item {
  border: 1px solid #e4e7ed;
  line-height: 2;
  margin-bottom: 10px;
  position: relative;
}

.upload-file-list .ele-upload-list__item-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: inherit;
}

.ele-upload-list__item-content-action .el-link {
  margin-right: 10px;
}
</style>